import { useActiveOrganizationId } from "@/contexts"
import { ApiQueryName } from "@/models/api"
import { useQueryKey } from "@/services/utils"
import { httpClient, toHttpError } from "@/utils/api"
import { useQuery } from "@tanstack/react-query"

import type { SankeyDrillResponse } from "../pages/schedule-misalignment-route/schedule-misalignment.types"
import { useWindowQuery } from "./use-window-query"
import type { WindowQuery } from "./use-window-query"

// The E2 deep-Sankey leaf
// (`/neat/insights/schedule-misalignment/{site_id}/{cycle_id}`) returns `Any` in
// insights-api's OpenAPI, so it's absent from the generated typed `client()`
// schema — use the shared axios `httpClient` (same as use-get-disaggregation).
// `cycle_id` is end-use-scoped: bare `"{enduse}"` (→ that end-use's months) or
// `"{enduse}:{parent_key}"` (parent shape picks the child grain). The response
// contract mirrors the BE snake_case `SankeyDrillResponse` verbatim — the
// interceptor allowlist keeps `api/v1/neat/insights/schedule-misalignment`
// un-camelCased.
const getSankeyDrill = async (
  organizationId: number,
  siteId: string,
  cycleId: string,
  win: WindowQuery
): Promise<SankeyDrillResponse> => {
  try {
    const res = await httpClient.get<SankeyDrillResponse>(
      `/api/v1/neat/insights/schedule-misalignment/${siteId}/${cycleId}`,
      { params: { organization_id: organizationId, ...win } }
    )
    return res.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

/**
 * Lazily resolves one level of the E2 deep Sankey for `(siteId, enduse,
 * parentKey)`. No fetch until `siteId` and `enduse` are set and `parentKey` is
 * non-null — an empty-string `parentKey` is valid and means "this end-use's
 * months" (root drill); `null` disables the query (level not yet reached). The
 * cycle_id sent is `enduse` when `parentKey` is `""`, else `${enduse}:${parentKey}`.
 */
export const useSankeyDrill = (
  siteId: string | null,
  enduse: string | null,
  parentKey: string | null
) => {
  const { activeOrganizationId } = useActiveOrganizationId()
  const win = useWindowQuery()
  const enabled =
    siteId != null &&
    enduse != null &&
    parentKey != null &&
    activeOrganizationId != null
  const cycleId =
    enduse != null && parentKey != null
      ? parentKey === ""
        ? enduse
        : `${enduse}:${parentKey}`
      : null
  // Reuse the E2 query name with a composite entity key so each (site, enduse,
  // parent) level caches independently and never collides with the master query
  // (entity key `null`) or another level.
  const queryKey = useQueryKey(
    ApiQueryName.ScheduleMisalignment,
    "getOne",
    cycleId != null ? `sankey:${siteId}:${cycleId}` : null,
    win
  )
  return useQuery<SankeyDrillResponse>({
    queryKey,
    enabled,
    queryFn: () =>
      getSankeyDrill(
        Number(activeOrganizationId),
        siteId as string,
        cycleId as string,
        win
      ),
  })
}
